import { useState, useEffect } from 'react';

export interface CMSContent {
  id: number;
  content_type: 'text' | 'image' | 'video' | 'json' | 'html';
  content: any;
  meta_data?: any;
  order_index: number;
}

interface PageContent {
  [sectionName: string]: CMSContent[];
}

interface AllPagesContent {
  [pageName: string]: PageContent;
}

interface CMSResponse {
  success: boolean;
  data: PageContent | AllPagesContent | CMSContent[];
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://browntech.co/bti-admin-panel/public/api';

export const useCMS = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPageContent = async (pageName: string): Promise<PageContent | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/cms/pages/${pageName}`);
      const data: CMSResponse = await response.json();
      
      if (data.success) {
        return data.data as PageContent;
      } else {
        throw new Error('Failed to fetch page content');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPages = async (): Promise<AllPagesContent | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/cms/pages`);
      const data: CMSResponse = await response.json();
      
      if (data.success) {
        return data.data as AllPagesContent;
      } else {
        throw new Error('Failed to fetch all pages content');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionContent = async (pageName: string, sectionName: string): Promise<CMSContent[] | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/cms/pages/${pageName}/sections/${sectionName}`);
      const data: CMSResponse = await response.json();
      
      if (data.success) {
        return data.data as CMSContent[];
      } else {
        throw new Error('Failed to fetch section content');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const searchContent = async (query: string, pageName?: string, sectionName?: string): Promise<CMSContent[] | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({ q: query });
      if (pageName) params.append('page', pageName);
      if (sectionName) params.append('section', sectionName);
      
      const response = await fetch(`${API_BASE_URL}/cms/search?${params}`);
      const data: CMSResponse = await response.json();
      
      if (data.success) {
        return data.data as CMSContent[];
      } else {
        throw new Error('Failed to search content');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    fetchPageContent,
    fetchAllPages,
    fetchSectionContent,
    searchContent
  };
};

// Hook for a specific page
export const usePageContent = (pageName: string) => {
  const [content, setContent] = useState<PageContent | null>(null);
  const { loading, error, fetchPageContent } = useCMS();

  useEffect(() => {
    if (pageName) {
      fetchPageContent(pageName).then(setContent);
    }
  }, [pageName]);

  return { content, loading, error };
};

// Hook for a specific section
export const useSectionContent = (pageName: string, sectionName: string, enabled: boolean = true) => {
  const [content, setContent] = useState<CMSContent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (enabled && pageName && sectionName) {
     
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        
        try {
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://browntech.co/bti-admin-panel/public/api';
          
          const response = await fetch(`${API_BASE_URL}/cms/pages/${pageName}/sections/${sectionName}`);
          const data = await response.json();
          
          if (data.success) {
            setContent(data.data);
          } else {
            setError('Failed to fetch content');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
          setLoading(false);
        }
      };
      
      fetchData();
    }
  }, [pageName, sectionName, enabled]);

  return { content, loading, error };
};

// Hook for capabilities portfolio sections (uses dedicated endpoints)
export const useCapabilitiesPortfolio = (pageName: string) => {
  const [content, setContent] = useState<CMSContent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pageName) {
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        
        try {
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://browntech.co/bti-admin-panel/public/api';
          
          // Map page names to their dedicated API endpoints
          const endpointMap: { [key: string]: string } = {
            'ai-sales': '/cms/capabilities/ai-sales/portfolio',
            'ui-ux': '/cms/capabilities/ui-ux/portfolio',
            'mobile-development': '/cms/capabilities/mobile-development/portfolio',
            'web-development': '/cms/capabilities/web-development/portfolio',
            'cloud-solution': '/cms/capabilities/cloud-solution/portfolio',
            'ai': '/cms/capabilities/ai/projects'
          };
          
          const endpoint = endpointMap[pageName];
          if (!endpoint) {
            throw new Error(`No dedicated endpoint found for page: ${pageName}`);
          }
          
          const response = await fetch(`${API_BASE_URL}${endpoint}`);
          const data = await response.json();
          
          if (data.success) {
            setContent(data.data);
          } else {
            setError('Failed to fetch content');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
          setLoading(false);
        }
      };
      
      fetchData();
    }
  }, [pageName]);

  return { content, loading, error };
};

// Utility functions for content processing
export const getTextContent = (content: CMSContent): string => {
  if (content.content_type === 'text') {
    return content.content;
  }
  if (content.content_type === 'json' && typeof content.content === 'object') {
    return JSON.stringify(content.content);
  }
  return '';
};

export const getJsonContent = (content: CMSContent): any => {
  if (content.content_type === 'json') {
    return content.content;
  }
  // Fallback: if content_type is not set but content exists and is an object/array, treat it as JSON
  if (content.content && (typeof content.content === 'object' || Array.isArray(content.content))) {
    return content.content;
  }
  return null;
};

export const getImageContent = (content: CMSContent): { url: string; alt: string } | null => {
  if (content.content_type === 'image' && content.content?.url) {
    return {
      url: content.content.url,
      alt: content.content.alt_text || content.meta_data?.alt_text || ''
    };
  }
  return null;
};

export const getVideoContent = (content: CMSContent): { url: string; thumbnail?: string } | null => {
  if (content.content_type === 'video' && content.content?.url) {
    return {
      url: content.content.url,
      thumbnail: content.content.thumbnail || content.meta_data?.thumbnail
    };
  }
  return null;
};

export const getHtmlContent = (content: CMSContent): string => {
  if (content.content_type === 'html') {
    return content.content;
  }
  return '';
}; 